// FHIR Resource Mappers
// Maps FHIR R4 resources to internal application types

import {
  FHIRPatient,
  FHIRCoverage,
  FHIREncounter,
  FHIRCondition,
  FHIRBundle,
  FHIRIdentifier,
  FHIRHumanName,
  FHIRCoverageClass,
  FHIRCodeableConcept,
} from './types';
import { WizardPatient, WizardCoverage, DiagnosisCode } from '@/lib/authorization/wizardTypes';

/**
 * Extract MRN from patient identifiers
 * Epic typically uses 'MR' type for medical record numbers
 */
function extractMRN(identifiers?: FHIRIdentifier[]): string {
  if (!identifiers) return '';
  
  // Look for MRN by type code
  const mrnIdentifier = identifiers.find(id => 
    id.type?.coding?.some(c => c.code === 'MR' || c.code === 'MRN')
  );
  
  if (mrnIdentifier?.value) {
    return mrnIdentifier.value;
  }
  
  // Fallback: look for Epic-specific MRN system
  const epicMRN = identifiers.find(id => 
    id.system?.includes('epic') && id.system?.includes('mrn')
  );
  
  if (epicMRN?.value) {
    return epicMRN.value;
  }
  
  // Last resort: use first identifier value
  return identifiers[0]?.value || '';
}

/**
 * Extract primary name from HumanName array
 * Prefers 'official' use, falls back to first name
 */
function extractName(names?: FHIRHumanName[]): { firstName: string; lastName: string } {
  if (!names || names.length === 0) {
    return { firstName: '', lastName: '' };
  }
  
  // Prefer official name
  const officialName = names.find(n => n.use === 'official') || names[0];
  
  return {
    firstName: officialName.given?.[0] || '',
    lastName: officialName.family || '',
  };
}

/**
 * Map FHIR Patient to WizardPatient
 */
export function mapFHIRPatientToWizardPatient(patient: FHIRPatient): WizardPatient {
  const { firstName, lastName } = extractName(patient.name);
  
  return {
    id: patient.id,
    mrn: extractMRN(patient.identifier),
    firstName,
    lastName,
    dob: patient.birthDate || '',
  };
}

/**
 * Extract payer name from Coverage
 */
function extractPayerName(coverage: FHIRCoverage): string {
  // Try to get from payor reference display
  if (coverage.payor && coverage.payor.length > 0) {
    const payorDisplay = coverage.payor[0].display;
    if (payorDisplay) return payorDisplay;
    
    // Parse from reference if needed
    const ref = coverage.payor[0].reference;
    if (ref) {
      // Reference format: "Organization/12345"
      const parts = ref.split('/');
      return parts[parts.length - 1];
    }
  }
  
  return 'Unknown Payer';
}

/**
 * Extract plan name from Coverage class array
 */
function extractPlanName(classes?: FHIRCoverageClass[]): string | undefined {
  if (!classes) return undefined;
  
  // Look for plan type
  const planClass = classes.find(c => 
    c.type?.coding?.some(coding => 
      coding.code === 'plan' || coding.code === 'group'
    )
  );
  
  return planClass?.name || planClass?.value;
}

/**
 * Extract member ID from Coverage
 */
function extractMemberId(coverage: FHIRCoverage): string | undefined {
  // Try subscriberId first
  if (coverage.subscriberId) {
    return coverage.subscriberId;
  }
  
  // Look in identifiers
  if (coverage.identifier) {
    const memberIdent = coverage.identifier.find(id => 
      id.type?.coding?.some(c => c.code === 'MB')
    );
    if (memberIdent?.value) {
      return memberIdent.value;
    }
    return coverage.identifier[0]?.value;
  }
  
  return undefined;
}

/**
 * Extract group number from Coverage class array
 */
function extractGroupNumber(classes?: FHIRCoverageClass[]): string | undefined {
  if (!classes) return undefined;
  
  const groupClass = classes.find(c => 
    c.type?.coding?.some(coding => coding.code === 'group')
  );
  
  return groupClass?.value;
}

/**
 * Map FHIR Coverage to WizardCoverage
 */
export function mapFHIRCoverageToWizardCoverage(coverage: FHIRCoverage): WizardCoverage {
  return {
    payerId: coverage.id,
    payerName: extractPayerName(coverage),
    planName: extractPlanName(coverage.class),
    memberId: extractMemberId(coverage),
    groupNumber: extractGroupNumber(coverage.class),
  };
}

/**
 * Extract ICD-10 codes from CodeableConcept
 */
function extractICD10Code(codeableConcept?: FHIRCodeableConcept): { code: string; description: string } | null {
  if (!codeableConcept) return null;
  
  // Look for ICD-10 coding
  const icd10Coding = codeableConcept.coding?.find(c => 
    c.system === 'http://hl7.org/fhir/sid/icd-10-cm' ||
    c.system?.includes('icd-10') ||
    c.system?.includes('ICD10')
  );
  
  if (icd10Coding?.code) {
    return {
      code: icd10Coding.code,
      description: icd10Coding.display || codeableConcept.text || '',
    };
  }
  
  // Fallback to any coding
  const anyCode = codeableConcept.coding?.[0];
  if (anyCode?.code) {
    return {
      code: anyCode.code,
      description: anyCode.display || codeableConcept.text || '',
    };
  }
  
  return null;
}

/**
 * Map FHIR Condition to DiagnosisCode
 */
export function mapFHIRConditionToDiagnosisCode(condition: FHIRCondition): DiagnosisCode | null {
  const codeInfo = extractICD10Code(condition.code);
  
  if (!codeInfo) return null;
  
  return {
    code: codeInfo.code,
    description: codeInfo.description,
  };
}

/**
 * Map multiple FHIR Conditions to DiagnosisCodes
 */
export function mapFHIRConditionsToDiagnosisCodes(conditions: FHIRCondition[]): DiagnosisCode[] {
  return conditions
    .map(mapFHIRConditionToDiagnosisCode)
    .filter((code): code is DiagnosisCode => code !== null);
}

/**
 * Extract encounter type
 */
export function mapFHIREncounterToContext(encounter: FHIREncounter): {
  encounterId: string;
  encounterDate: string;
  encounterType: string;
  diagnoses: DiagnosisCode[];
} {
  // Get type display
  const typeDisplay = encounter.type?.[0]?.text || 
    encounter.type?.[0]?.coding?.[0]?.display ||
    encounter.class?.display ||
    'Unknown';
  
  // Get period start
  const encounterDate = encounter.period?.start?.split('T')[0] || '';
  
  return {
    encounterId: encounter.id,
    encounterDate,
    encounterType: typeDisplay,
    diagnoses: [], // Will be populated from encounter.diagnosis references
  };
}

/**
 * Helper to extract resources from a FHIR Bundle
 */
export function extractResourcesFromBundle<T>(
  bundle: FHIRBundle<T>,
  resourceType: string
): T[] {
  if (!bundle.entry) return [];
  
  return bundle.entry
    .filter(entry => {
      const resource = entry.resource as { resourceType?: string };
      return resource?.resourceType === resourceType;
    })
    .map(entry => entry.resource as T)
    .filter((resource): resource is T => resource !== undefined);
}

/**
 * Context aggregated from SMART launch
 */
export interface AggregatedFHIRContext {
  patient: WizardPatient | null;
  coverage: WizardCoverage | null;
  diagnoses: DiagnosisCode[];
  encounter?: {
    id: string;
    date: string;
    type: string;
  };
  rawResources: {
    patient?: FHIRPatient;
    coverages?: FHIRCoverage[];
    conditions?: FHIRCondition[];
    encounter?: FHIREncounter;
  };
}

/**
 * Aggregate all FHIR data into a unified context for the wizard
 */
export function aggregateFHIRContext(
  patient: FHIRPatient | null,
  coverages: FHIRCoverage[] | null,
  conditions: FHIRCondition[] | null,
  encounter: FHIREncounter | null
): AggregatedFHIRContext {
  const context: AggregatedFHIRContext = {
    patient: patient ? mapFHIRPatientToWizardPatient(patient) : null,
    coverage: null,
    diagnoses: [],
    rawResources: {
      patient: patient || undefined,
      coverages: coverages || undefined,
      conditions: conditions || undefined,
      encounter: encounter || undefined,
    },
  };
  
  // Get primary/active coverage
  if (coverages && coverages.length > 0) {
    const activeCoverage = coverages.find(c => c.status === 'active') || coverages[0];
    context.coverage = mapFHIRCoverageToWizardCoverage(activeCoverage);
  }
  
  // Map conditions to diagnosis codes
  if (conditions && conditions.length > 0) {
    context.diagnoses = mapFHIRConditionsToDiagnosisCodes(conditions);
  }
  
  // Add encounter context
  if (encounter) {
    const encounterContext = mapFHIREncounterToContext(encounter);
    context.encounter = {
      id: encounterContext.encounterId,
      date: encounterContext.encounterDate,
      type: encounterContext.encounterType,
    };
  }
  
  return context;
}
